import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/auth-js';
import { adminApi } from '@l2u/web-shared';
import { getAdminSupabase } from '@l2u/web-shared';
import { handleMeFailure } from './meFailure.ts';
import type { AdminUser } from '@l2u/web-shared';
import {
  authEventAction,
  createSessionUserLoader,
  restoredTeamRegion,
  setAdminAccessToken,
  useBranding,
} from '@l2u/web-shared';

type AdminAuthValue = {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  /** Sidebar-selected team region ID; null = "All". Persisted per user. */
  activeTeamRegion: string | null;
  setActiveTeamRegion: (next: string | null) => void;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
  /** Update the signed-in user's name after a successful self-rename. */
  setOwnName: (name: string) => void;
};

const AdminAuthContext = createContext<AdminAuthValue>({
  user: null,
  session: null,
  loading: true,
  activeTeamRegion: null,
  setActiveTeamRegion: () => {},
  signInWithPassword: async () => {},
  signOut: async () => {},
  reload: async () => {},
  setOwnName: () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTeamRegion, setActiveTeamRegionState] = useState<string | null>(null);
  const { reload: reloadBranding } = useBranding();

  // BrandingProvider mounts (and does its first, token-less fetch) above this
  // provider, so the shell initially shows the platform brand. Once the signed-
  // in USER changes — session restored, sign-in, sign-out — re-fetch so the
  // shell picks up this tenant's branding (the API resolves the tenant from the
  // bearer token). Keyed on the user, not the token: a token refresh or a tab
  // refocus cannot change which tenant the bearer resolves to, and re-fetching
  // on each was a branding round trip every hour and every refocus. Skip the
  // first run: that fetch already happened in BrandingProvider's own mount
  // effect.
  const brandingReloadPrimed = useRef(false);
  const sessionUserId = session?.user.id ?? null;
  useEffect(() => {
    if (!brandingReloadPrimed.current) {
      brandingReloadPrimed.current = true;
      return;
    }
    void reloadBranding();
  }, [sessionUserId, reloadBranding]);

  const storageKey = (userId: string) => `l2u.activeTeamRegion.${userId}`;

  const setActiveTeamRegion = useCallback(
    (next: string | null) => {
      setActiveTeamRegionState(next);
      if (!user) return;
      try {
        if (next === null) localStorage.removeItem(storageKey(user.id));
        else localStorage.setItem(storageKey(user.id), next);
      } catch {
        // storage unavailable (private mode) — selection just won't persist
      }
    },
    [user],
  );

  // One /me per signed-in user, shared by the getSession restore, the auth
  // listener, sign-in and reload — see sessionUserLoad.ts for the events this
  // used to answer with a fresh /me each time.
  const [userLoader] = useState(createSessionUserLoader);

  const loadUser = useCallback(
    (currentSession: Session | null, opts?: { force?: boolean }): Promise<void> =>
      userLoader.run(
        currentSession?.user.id ?? null,
        async (isCurrent) => {
          if (!currentSession) {
            setUser(null);
            setActiveTeamRegionState(null);
            return true;
          }
          try {
            const res = await adminApi.get<{ user: AdminUser }>('/api/auth/admin/me');
            // Overtaken by a load for another user (or a sign-out) while in
            // flight: its answer is no longer this provider's to apply.
            if (!isCurrent()) return true;
            setUser(res.user);
            try {
              // The stored value is a region ID (a pre-hierarchy stored NAME
              // simply won't match and resets to All), restored against the
              // admin's scope — see restoredTeamRegion. The shell's option list
              // reconciles deleted regions back to All.
              setActiveTeamRegionState(
                restoredTeamRegion(
                  localStorage.getItem(`l2u.activeTeamRegion.${res.user.id}`),
                  res.user,
                ),
              );
            } catch {
              setActiveTeamRegionState(null);
            }
            return true;
          } catch (err) {
            if (!isCurrent()) return false;
            // A 403 means the Supabase user has no matching public.users row —
            // not an admin — and is the only failure that should end the
            // session. See meFailure.ts for why a 401 must NOT, and why the
            // sign-out is local.
            await handleMeFailure(getAdminSupabase().auth, err);
            setUser(null);
            return false;
          }
        },
        opts,
      ),
    [userLoader],
  );

  useEffect(() => {
    let mounted = true;
    const supabase = (() => {
      try {
        return getAdminSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      const initial = data.session ?? null;
      setSession(initial);
      setAdminAccessToken(initial?.access_token ?? null);
      await loadUser(initial);
      if (mounted) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      const action = authEventAction(event, next?.user.id ?? null, userLoader.loadedFor());
      if (action === 'ignore') return;
      setSession(next ?? null);
      setAdminAccessToken(next?.access_token ?? null);
      if (action === 'load-user') void loadUser(next ?? null);
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadUser, userLoader]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const supabase = getAdminSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setSession(data.session);
      setAdminAccessToken(data.session?.access_token ?? null);
      await loadUser(data.session);
    },
    [loadUser],
  );

  const signOut = useCallback(async () => {
    try {
      const supabase = getAdminSupabase();
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setUser(null);
      setAdminAccessToken(null);
      // Clear the in-memory selection only; the persisted per-user value
      // should survive re-login.
      setActiveTeamRegionState(null);
      // Record "nobody" as loaded even if signOut() failed before announcing
      // SIGNED_OUT — otherwise the next SIGNED_IN for the same user would read
      // as a refocus and never load them.
      void loadUser(null);
    }
  }, [loadUser]);

  const reload = useCallback(async () => {
    await loadUser(session, { force: true });
  }, [loadUser, session]);

  // After a successful rename of the signed-in user. Patches the name in place
  // rather than calling reload(): loadUser clears the user on ANY /me failure
  // (a transient 5xx would drop the admin shell mid-session) and re-derives the
  // active region from storage.
  const setOwnName = useCallback((name: string) => {
    setUser((u) => (u ? { ...u, name } : u));
  }, []);

  const value = useMemo<AdminAuthValue>(
    () => ({
      user,
      session,
      loading,
      activeTeamRegion,
      setActiveTeamRegion,
      signInWithPassword,
      signOut,
      reload,
      setOwnName,
    }),
    [
      user,
      session,
      loading,
      activeTeamRegion,
      setActiveTeamRegion,
      signInWithPassword,
      signOut,
      reload,
      setOwnName,
    ],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  return useContext(AdminAuthContext);
}
